import { useCallback, useMemo, useState } from 'react';

import { deriveTipAmount, presetTipCents, resolveTipBaseDollars } from './checkoutIdempotency';

/**
 * The checkout's tip — ONE derivation, from ONE base, for the tiles and the
 * charge.
 *
 * The base is the server-priced subtotal once a preview has returned one, the
 * cart's before then (`resolveTipBaseDollars`). The server reprices every line
 * at preview and at checkout, so its subtotal is what the guest is shown; the
 * tiles used to read it while the charge read the cart's, and a repriced cart
 * offered one tip and charged another.
 *
 * The preview is requested WITH the tip, so its subtotal cannot be an input to
 * this hook's first call. The page reports it back through
 * `notePreviewSubtotal`: the first preview is taken on the cart base, the
 * reported subtotal moves the base, and the re-preview that triggers carries
 * the final tip. The subtotal does not depend on the tip, so it settles there,
 * and the page blocks submit while that re-preview is in flight
 * (`isPreviewStale`) — so the `expectedTotal` it locks always carries the tip
 * it sends, and the server's `subtotal + tax + tip` (the same client tip, the
 * same server subtotal) agrees with it.
 */
export function useCheckoutTip(opts: {
    isTestMode: boolean;
    cartSubtotal: number | null | undefined;
    selectedTip: number;
    customTip: string;
}): {
    tipAmount: number;
    tipCentsFor: (percentage: number) => number;
    tipBaseDollars: number;
    notePreviewSubtotal: (subtotal: number | null | undefined) => void;
} {
    const { isTestMode, cartSubtotal, selectedTip, customTip } = opts;
    const [serverSubtotal, setServerSubtotal] = useState<number | null>(null);
    const tipBaseDollars = resolveTipBaseDollars(serverSubtotal, cartSubtotal);

    const tipCentsFor = useCallback(
        (percentage: number) => presetTipCents(tipBaseDollars, percentage),
        [tipBaseDollars],
    );

    const tipAmount = useMemo(
        () => deriveTipAmount({ isTestMode, baseAmount: tipBaseDollars, selectedTip, customTip }),
        [isTestMode, tipBaseDollars, selectedTip, customTip],
    );

    const notePreviewSubtotal = useCallback((subtotal: number | null | undefined) => {
        if (typeof subtotal === 'number' && Number.isFinite(subtotal)) setServerSubtotal(subtotal);
    }, []);

    return { tipAmount, tipCentsFor, tipBaseDollars, notePreviewSubtotal };
}
